import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ReportHeader } from '../../header/report-header';
import { ProgramFinancialSideBarFilter } from './side-bar-filter/program-financial-side-bar-filter';
import Colors from '../../../../utils/colors';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import './program-financial-comparison-by-branches.css';
import { ProgramFinancialSideBarFiltersDatamodelType } from './side-bar-filter/program-financial-side-bar-filters-datamodel.type';
import { getProgramFinancialDataOperations } from './operations/get-program-financial-data-operations';
import { ProgramComparisonAmongBranchesDb } from '../../../../api/types/reports/finanace/program-comparison-among-branches';
import { ProgramStatus } from '../../../../api/types/programs/program-status';

export const ProgramFinancialComparisonByBranches = observer(() => {
  const [financialData, setFinancialData] = useState<
    ProgramComparisonAmongBranchesDb[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] =
    useState<ProgramFinancialSideBarFiltersDatamodelType>();

  const handleSearch = async (
    filters: ProgramFinancialSideBarFiltersDatamodelType,
  ) => {
    setFilters(filters);
    await getProgramFinancialDataOperations(
      filters,
      setFinancialData,
      setLoading,
    );
  };

  useEffect(() => {
    /*    // Generate mock data based on actual branches
    const mockData = globalSettingsBranches.branches.map((branch) => ({
      branchId: branch.id,
      branchName: branch.branchName,
      abstractProgramId: 1,
      abstractProgramName: 'הנדסת תוכנה',
      programId: 1,
      annualSerialProgramNumber: 1,
      programStartYear: 2023,
      programStatus: ProgramStatus.ACTIVE,
      actualHoursTotal: Math.floor(Math.random() * 1000) + 500,
      actualTeachingCostTotal: Math.floor(Math.random() * 50000) + 20000,
      totalRevenue: Math.floor(Math.random() * 100000) + 50000,
    }));

    setFinancialData(mockData);*/
    /*    handleSearch({
      branchIds: [],
      abstractProgramIds: [],
      startYears: [],
      studyYears: [],
    });*/
  }, []);

  // Calculate totals - aggregate from nested programYears structure
  const totalActualHours = financialData.reduce((sum, item) => {
    const branchTotal = item.programYears.reduce(
      (yearSum, year) => yearSum + year.financialInformation.totalHours,
      0,
    );
    return sum + branchTotal;
  }, 0);

  const totalTeachingCost = financialData.reduce((sum, item) => {
    const branchTotal = item.programYears.reduce(
      (yearSum, year) => yearSum + year.financialInformation.totalCost,
      0,
    );
    return sum + branchTotal;
  }, 0);

  const totalRevenue = financialData.reduce((sum, item) => {
    const branchTotal = item.programYears.reduce(
      (yearSum, year) => yearSum + year.financialInformation.totalPaymentIncome,
      0,
    );
    return sum + branchTotal;
  }, 0);

  // Prepare data for the chart - aggregate financial data per branch
  const chartData = {
    labels: financialData.map((item) => item.branchName),
    datasets: [
      {
        label: 'סך השעות שבוצעו בפועל',
        data: financialData.map((item) =>
          item.programYears.reduce(
            (sum, year) => sum + year.financialInformation.totalHours,
            0,
          ),
        ),
        backgroundColor: Colors.approveText2,
        borderWidth: 0,
      },
      {
        label: 'סך עלות הוראה בפועל',
        data: financialData.map((item) =>
          item.programYears.reduce(
            (sum, year) => sum + year.financialInformation.totalCost,
            0,
          ),
        ),
        backgroundColor: Colors.warningYelloText,
        borderWidth: 0,
      },
      {
        label: 'סך הכנסות',
        data: financialData.map((item) =>
          item.programYears.reduce(
            (sum, year) => sum + year.financialInformation.totalPaymentIncome,
            0,
          ),
        ),
        backgroundColor: Colors.dangerText2,
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'סכום (₪)',
        },
        grid: {
          color: '#f0f0f0',
        },
      },
      x: {
        title: {
          display: true,
          text: 'שלוחה',
        },
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: string | number): string | string[] {
            const label = this.getLabelForValue(value);
            if (label.length > 10) {
              return label.match(/.{1,10}/g)?.join('\n') || label;
            }
            return label;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
    maintainAspectRatio: false,
  };

  const SummaryCard = ({
    title,
    value,
    color,
    icon,
    trend,
  }: {
    title: string;
    value: number;
    color: string;
    icon: string;
    trend?: { value: number; isPositive: boolean };
  }) => (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: 1,
        border: '1px solid #e9ecef',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '4px',
          height: '100%',
          background: color,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: `${color}10`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: color,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              margin: 0,
              fontSize: '14px',
              color: '#6c757d',
              fontWeight: 500,
            }}
          >
            {title}
          </h3>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#212529',
              display: 'flex',
              alignItems: 'baseline',
              gap: '4px',
            }}
          >
            {value.toLocaleString()}
            <span
              style={{ fontSize: '14px', fontWeight: 400, color: '#6c757d' }}
            >
              ₪
            </span>
          </div>
        </div>
      </div>
      {trend && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: trend.isPositive ? Colors.approveText2 : Colors.dangerText2,
          }}
        >
          <span>{trend.isPositive ? '↑' : '↓'}</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <ReportHeader
        title="דו״ח השוואת פיננסית בין תוכניות"
        description="דו״ח זה מציג השוואה פיננסית מקיפה של תכנית לימודים בין שלוחות שונות, כולל נתוני שעות לימוד, עלויות והכנסות."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'השוואה פיננסית של תכנית בין שלוחות' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="financial"
      />

      <div style={{ display: 'flex', gap: '24px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <ProgramFinancialSideBarFilter onSearch={handleSearch} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <SummaryCard
              title="סך השעות שבוצעו בפועל"
              value={totalActualHours}
              color={Colors.approveText2}
              icon="🕒"
              trend={{ value: 0, isPositive: true }}
            />
            <SummaryCard
              title="סך עלות הוראה בפועל"
              value={totalTeachingCost}
              color={Colors.warningYelloText}
              icon="💼"
              trend={{ value: 0, isPositive: false }}
            />
            <SummaryCard
              title="סך הכנסות"
              value={totalRevenue}
              color={Colors.dangerText2}
              icon="📈"
              trend={{ value: 0, isPositive: true }}
            />
          </div>

          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              border: '1px solid #e9ecef',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: '#495057',
                }}
              >
                📊
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    color: '#212529',
                    fontWeight: 600,
                  }}
                >
                  השוואת נתונים פיננסיים בין השלוחות
                </h3>
                <p
                  style={{
                    margin: '4px 0 0 0',
                    fontSize: '12px',
                    color: '#6c757d',
                  }}
                >
                  נתונים מעודכנים נכון ל-
                  {new Date().toLocaleDateString('he-IL')}
                </p>
              </div>
            </div>
            <div>
              <Chart type="bar" data={chartData} options={chartOptions} />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <DataTable
              value={financialData}
              rows={10}
              rowHover
              rowClassName={() => 'cursor-pointer'}
              rowsPerPageOptions={[5, 10, 25, 50]}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
              }
              dataKey="branchId"
              scrollable
              scrollHeight="calc(100vh - 674px)"
              tableStyle={{
                direction: 'rtl',
              }}
              className="program-financial-table"
            >
              <Column
                field="branchName"
                header="שלוחה"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
              />
              <Column
                field="abstractProgramName"
                header="תכנית לימודים"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
              />
              <Column
                field="programStartYear"
                header="שנת התחלה"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
              />
              <Column
                field="annualSerialProgramNumber"
                header="מספר תוכנית שנתי"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
              />
              <Column
                header="סך השעות שבוצעו בפועל"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
                body={(rowData: ProgramComparisonAmongBranchesDb) => {
                  const totalHours = rowData.programYears.reduce(
                    (sum, year) => sum + year.financialInformation.totalHours,
                    0,
                  );
                  return totalHours.toLocaleString();
                }}
              />
              <Column
                header="סך עלות הוראה בפועל"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
                body={(rowData: ProgramComparisonAmongBranchesDb) => {
                  const totalCost = rowData.programYears.reduce(
                    (sum, year) => sum + year.financialInformation.totalCost,
                    0,
                  );
                  return `${totalCost.toLocaleString()} ₪`;
                }}
              />
              <Column
                header="סך הכנסות"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
                body={(rowData: ProgramComparisonAmongBranchesDb) => {
                  const totalRevenue = rowData.programYears.reduce(
                    (sum, year) =>
                      sum + year.financialInformation.totalPaymentIncome,
                    0,
                  );
                  return `${totalRevenue.toLocaleString()} ₪`;
                }}
              />
              <Column
                field="programStatus"
                header="סטטוס"
                style={{ textAlign: 'right' }}
                headerStyle={{ textAlign: 'right' }}
                body={(rowData) => (
                  <span
                    style={{
                      color:
                        rowData.programStatus === ProgramStatus.ACTIVE
                          ? Colors.approveText2
                          : rowData.programStatus === ProgramStatus.INIT
                            ? Colors.warningYelloText
                            : Colors.dangerText2,
                      fontWeight: 'bold',
                    }}
                  >
                    {ProgramStatus.getDisplayText(rowData.programStatus)}
                  </span>
                )}
              />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
});
